import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Hash } from 'lucide-react';
import { toast } from 'sonner';

const JoinEventModal = ({ onClose }) => {
  const navigate = useNavigate();
  const [eventCode, setEventCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleJoin = async () => {
    if (!eventCode || eventCode.trim().length === 0) {
      toast.error('Please enter a valid event code');
      return;
    }

    // Validate format: should be either old formats or new custom prefix format
    const oldSixDigit = /^\d{6}$/.test(eventCode);
    const oldMobileFormat = /^\d{10}_\d{6}$/.test(eventCode);
    const newCustomFormat = /^[a-zA-Z0-9]{6}_\d{6}$/.test(eventCode); // 6 alphanumeric chars + underscore + 6 digits
    
    if (!oldSixDigit && !oldMobileFormat && !newCustomFormat) {
      toast.error('Please enter a valid event code (e.g., ABC123_678901 or THIRUV_123456)');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/events/join/${eventCode}`);
      if (response.data) {
        toast.success('Event found!');
        navigate(`/event/${response.data.id}`);
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Event not found with this code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="join-event-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Join an Event
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="eventCode" className="text-gray-900 font-semibold">Event Code</Label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                id="eventCode"
                type="text"
                placeholder="e.g., THIRUV_123456 or ABC123_678901"
                value={eventCode}
                onChange={(e) => setEventCode(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toUpperCase().slice(0, 13))}
                className="pl-10 text-center text-lg tracking-wide font-semibold text-gray-900"
                maxLength={13}
                data-testid="event-code-input"
              />
            </div>
            <p className="text-xs text-gray-700 mt-1 text-center font-medium">
              Enter the event code shared by the host
            </p>
          </div>

          <Button
            className="w-full bg-gradient-to-r from-contribo-navy to-contribo-gold text-white"
            onClick={handleJoin}
            disabled={loading}
            data-testid="join-event-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Joining...
              </>
            ) : (
              'Join Event'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JoinEventModal;
